import { useEffect, useRef, useState } from 'react';

interface UseSpinDelayOptions {
  /**
   * Delay in ms before showing the spinner
   * @default 500
   */
  delay?: number;
  /**
   * Minimum duration in ms to show the spinner once it appears
   * @default 200
   */
  minDuration?: number;
}

/**
 * Hook to delay showing a loading spinner and ensure it stays visible for a minimum duration.
 * This prevents flickering for fast operations and jarring UI changes.
 *
 * Based on the spin-delay pattern:
 * - Don't show spinner immediately (wait for `delay` ms)
 * - Once shown, keep visible for at least `minDuration` ms
 *
 * @param loading - Whether the operation is currently loading
 * @param options - Configuration options
 * @returns boolean - Whether to show the spinner
 *
 * @example
 * ```tsx
 * const showSpinner = useSpinDelay(isLoading);
 * return showSpinner ? <Spinner /> : <Content />;
 * ```
 */
export function useSpinDelay(loading: boolean, options: UseSpinDelayOptions = {}): boolean {
  const { delay = 500, minDuration = 200 } = options;

  const [showSpinner, setShowSpinner] = useState(false);
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shownAtRef = useRef<number | null>(null);

  useEffect(() => {
    // Clear any pending timers
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }

    if (loading) {
      // Only set timer if spinner isn't already showing (ref will be non-null if showing)
      if (shownAtRef.current === null) {
        showTimerRef.current = setTimeout(() => {
          setShowSpinner(true);
          shownAtRef.current = Date.now();
          showTimerRef.current = null;
        }, delay);
      }
    } else {
      // Loading stopped - check if spinner is visible
      if (shownAtRef.current !== null) {
        const elapsed = Date.now() - shownAtRef.current;
        const remaining = minDuration - elapsed;

        if (remaining > 0) {
          // Need to wait for minimum duration
          hideTimerRef.current = setTimeout(() => {
            setShowSpinner(false);
            shownAtRef.current = null;
            hideTimerRef.current = null;
          }, remaining);
        } else {
          // Minimum duration already passed, hide immediately
          setShowSpinner(false);
          shownAtRef.current = null;
        }
      }
    }

    return () => {
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
      }
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
    };
  }, [loading, delay, minDuration]);

  return showSpinner;
}
